import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ReceiptLong,
  Payments,
  Block,
  UploadFile,
  Visibility,
  FactCheck,
  Gavel,
  Undo,
  Replay,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { documentoService } from '@/services/documentoService'
import { LancamentoComissao, NotaFiscalComissao } from '@/types/comissao'
import EmitirNotaDialog, { EXTENSOES_ACEITAS, TIPO_ENTIDADE_NF_COMISSAO } from './EmitirNotaDialog'
import DadosNotaDialog, { ROTULO_LEITURA } from './DadosNotaDialog'
import ConferirNotaDialog from './ConferirNotaDialog'
import AprovarFiscalDialog from './AprovarFiscalDialog'
import { mensagemErroNota } from './errosNotaFiscal'
import { brl } from './estadoFinanceiro'
import { STATUS_NOTA, pendenciasAbertas, rotuloPendencia, temPendenciaAberta } from './notaFiscalRotulos'
import { SeloArquivoNf, arquivoRecusado, motivoArquivoNaoLiberado } from './verificacaoArquivoNf'

/**
 * Aba "Notas fiscais" do Financeiro (`/financeiro?aba=nf`, A15). A nota é emitida NO SISTEMA
 * DO CONSULTOR e registrada com o arquivo; a Originei confere, aprova, paga ou recusa.
 *
 * AN-9 (L10 · Q36) — o fluxo passou a ter etapas e bloqueios, todos decididos no backend:
 * ENVIADA → (Conferir) DADOS_CONFIRMADOS → (Aprovar fiscal) APROVADA_FISCAL → (Pagar) PAGA.
 * A conferência humana roda o motor de divergências e abre pendências; com pendência aberta
 * não se aprova nem se paga (os botões aparecem desabilitados, com o motivo). O consultor vê
 * as pendências e pode "Enviar arquivo corrigido" ou "Retirar e reemitir".
 *
 * `?nota={id}` (destino da Central de pendências) destaca e rola até a nota.
 *
 * Nada é calculado aqui: estado, pendências e valores vêm prontos do backend.
 *
 * Onda 5 (AN-11 §3.5): o arquivo anexado tem verificação própria (`documentoVerificacao`).
 * Selo "Arquivo em verificação"/"Arquivo recusado — anexe novamente" na coluna Arquivo; com
 * arquivo recusado volta o "Anexar arquivo"; Conferir e Aprovar fiscal ficam desabilitados,
 * com o motivo, enquanto o arquivo não está VERIFICADO/LEGADO (o servidor responde 409).
 */

/**
 * VC-10 (Onda 1 da Visão do Consultor): para o consultor o "Registrar nota" aparece
 * SEMPRE — desabilitado, com esta explicação, enquanto não há comissão confirmada livre.
 * Sem NF avulsa (Q-VC5 provisória): a nota só cobre lançamento CONFIRMADO.
 */
export const EXPLICACAO_NOTA_INDISPONIVEL =
  'Disponível quando a Originei confirmar uma comissão sua — aí você emite a nota e anexa o PDF ou XML aqui.'

/** Enquanto há leitura em andamento a lista se atualiza sozinha — até ~2 min. */
const INTERVALO_LEITURA_MS = 5000
const MAX_ATUALIZACOES_LEITURA = 24

const data = (iso?: string | null): string => (iso ? dayjs(iso).format('DD/MM/YYYY') : '—')

/** Estados em que a nota ainda pode ser recusada (AN-9 §3). */
const RECUSAVEL = new Set(['ENVIADA', 'DADOS_CONFIRMADOS', 'APROVADA_FISCAL'])
/** Estados que aceitam conferência e arquivo novo. */
const EM_CONFERENCIA = new Set(['ENVIADA', 'DADOS_CONFIRMADOS'])

/** Por que "Pagar" está desabilitado — `null` quando pode pagar. */
export const motivoNaoPaga = (n: NotaFiscalComissao): string | null => {
  const abertas = pendenciasAbertas(n)
  if (temPendenciaAberta(n)) {
    const rotulos = abertas.map(rotuloPendencia).join(', ')
    return rotulos ? `Pendência aberta: ${rotulos}.` : 'Há pendência aberta.'
  }
  if (n.status === 'ENVIADA') return 'Confira os dados da nota antes de pagar.'
  if (n.status === 'DADOS_CONFIRMADOS') return 'Falta a aprovação fiscal.'
  return null
}

/** Por que "Conferir" está desabilitado — `null` quando pode conferir. */
export const motivoNaoConfere = (n: NotaFiscalComissao): string | null => {
  if (!n.documentoId) return 'Aguardando o arquivo da nota.'
  const arquivo = motivoArquivoNaoLiberado(n)
  if (arquivo) return arquivo
  if (n.extracao?.status === 'PENDENTE') return 'Aguardando a leitura do arquivo terminar.'
  return null
}

/** Por que "Aprovar fiscal" está desabilitado — `null` quando pode aprovar. */
export const motivoNaoAprova = (n: NotaFiscalComissao): string | null =>
  motivoArquivoNaoLiberado(n) ??
  (temPendenciaAberta(n) ? 'Resolva as pendências antes de aprovar.' : null)

/** Botão desabilitado não dispara eventos: o Tooltip com o motivo mora num <span>. */
const ComMotivo: FC<{ motivo: string | null; children: ReactNode }> = ({ motivo, children }) =>
  motivo ? (
    <Tooltip title={motivo}>
      <span>{children}</span>
    </Tooltip>
  ) : (
    <>{children}</>
  )

interface Props {
  podeOperar: boolean
  /**
   * Persona consultor (critério `ehConsultor` de `menu.ts`). Sem o dado, quem não opera
   * (não é back-office) é tratado como consultor.
   */
  consultor?: boolean
  /** Avisa o pai que uma nota mudou (paga/recusada) — o extrato da outra aba recarrega. */
  onMudou?: () => void
}

const NotasFiscaisPanel: FC<Props> = ({ podeOperar, consultor = !podeOperar, onMudou }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [searchParams] = useSearchParams()
  const notaDestacada = searchParams.get('nota')
  const linhaDestacada = useRef<HTMLTableRowElement | null>(null)

  const [notas, setNotas] = useState<NotaFiscalComissao[]>([])
  const [disponiveis, setDisponiveis] = useState<LancamentoComissao[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [registrando, setRegistrando] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const [liquidando, setLiquidando] = useState<NotaFiscalComissao | null>(null)
  const [dataPagamento, setDataPagamento] = useState(dayjs().format('YYYY-MM-DD'))
  const [recusando, setRecusando] = useState<NotaFiscalComissao | null>(null)
  const [motivo, setMotivo] = useState('')
  const [vendo, setVendo] = useState<NotaFiscalComissao | null>(null)
  const [conferindo, setConferindo] = useState<NotaFiscalComissao | null>(null)
  const [aprovando, setAprovando] = useState<NotaFiscalComissao | null>(null)
  const [revogando, setRevogando] = useState<NotaFiscalComissao | null>(null)
  const [motivoRevogacao, setMotivoRevogacao] = useState('')
  const [retirando, setRetirando] = useState<NotaFiscalComissao | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const [lista, extrato] = await Promise.all([
        comissaoService.notasFiscais(),
        comissaoService.extrato('CONFIRMADA'),
      ])
      setNotas(lista)
      // O backend já marca o que pode entrar numa nota; filtrar por esse sinalizador evita
      // reimplementar aqui a regra de "confirmada e ainda não coberta".
      setDisponiveis(extrato.filter((l) => l.disponivelParaNota))
    } catch {
      setErro('Não foi possível carregar as notas fiscais.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // Deep link da Central (`?nota={id}`): rola até a linha destacada quando a lista chega.
  useEffect(() => {
    if (!carregando && notaDestacada) linhaDestacada.current?.scrollIntoView?.({ block: 'center' })
  }, [carregando, notaDestacada])

  // Leitura (ou, Onda 5, verificação do arquivo) em andamento: só a lista de notas é
  // recarregada, em silêncio (sem spinner).
  const lendo = notas.some(
    (n) => n.extracao?.status === 'PENDENTE' || (!!n.documentoId && n.documentoVerificacao === 'PENDENTE')
  )
  useEffect(() => {
    if (!lendo) return
    let vezes = 0
    const id = setInterval(() => {
      vezes += 1
      if (vezes > MAX_ATUALIZACOES_LEITURA) {
        clearInterval(id)
        return
      }
      comissaoService
        .notasFiscais()
        .then(setNotas)
        .catch(() => undefined)
    }, INTERVALO_LEITURA_MS)
    return () => clearInterval(id)
  }, [lendo])

  const mudou = async () => {
    await carregar()
    onMudou?.()
  }

  const anexar = async (nota: NotaFiscalComissao, arquivo: File, corrigido = false) => {
    setSalvando(true)
    try {
      const doc = await documentoService.upload(
        arquivo,
        'NOTA_FISCAL',
        nota.id,
        TIPO_ENTIDADE_NF_COMISSAO
      )
      await comissaoService.anexarDocumentoNota(nota.id, doc.id)
      enqueueSnackbar(
        corrigido
          ? 'Arquivo corrigido enviado — a nota volta para a conferência do Financeiro.'
          : 'Arquivo anexado à nota.',
        { variant: 'success' }
      )
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao anexar o arquivo.'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const liquidar = async () => {
    if (!liquidando) return
    setSalvando(true)
    try {
      await comissaoService.liquidarNota(liquidando.id, dataPagamento)
      enqueueSnackbar('Pagamento registrado.', { variant: 'success' })
      setLiquidando(null)
      await mudou()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao registrar o pagamento.'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const recusar = async () => {
    if (!recusando) return
    setSalvando(true)
    try {
      await comissaoService.recusarNota(recusando.id, motivo.trim())
      enqueueSnackbar('Nota recusada — as comissões voltaram ao disponível.', {
        variant: 'success',
      })
      setRecusando(null)
      setMotivo('')
      await mudou()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao recusar a nota.'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const revogar = async () => {
    if (!revogando) return
    setSalvando(true)
    try {
      await comissaoService.revogarAprovacaoNota(revogando.id, motivoRevogacao.trim())
      enqueueSnackbar('Aprovação fiscal revogada — a nota voltou para "Dados confirmados".', {
        variant: 'success',
      })
      setRevogando(null)
      setMotivoRevogacao('')
      await mudou()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao revogar a aprovação.'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const retirar = async () => {
    if (!retirando) return
    setSalvando(true)
    try {
      await comissaoService.retirarNota(retirando.id)
      enqueueSnackbar('Nota retirada — as comissões voltaram ao disponível para você reemitir.', {
        variant: 'success',
      })
      setRetirando(null)
      await mudou()
    } catch (e) {
      enqueueSnackbar(mensagemErroNota(e, 'Falha ao retirar a nota.'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  if (carregando) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando notas fiscais" />
      </Box>
    )
  }

  return (
    <Box>
      {erro && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button size="small" color="inherit" onClick={() => void carregar()}>
              Tentar de novo
            </Button>
          }
        >
          {erro}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          {disponiveis.length > 0 ? (
            // Sem somar na tela: o total disponível é o KPI "A receber" do resumo, acima.
            <Alert severity="success">
              {disponiveis.length === 1
                ? 'Há 1 comissão confirmada disponível para nota fiscal.'
                : `Há ${disponiveis.length} comissões confirmadas disponíveis para nota fiscal.`}{' '}
              Não há valor mínimo para emitir.
            </Alert>
          ) : (
            <Alert severity="info">
              Nenhuma comissão disponível para nota no momento. Os valores ficam disponíveis
              quando a Originei confirma o recebimento.
            </Alert>
          )}
        </Box>
        {disponiveis.length > 0 ? (
          <Button
            variant="contained"
            startIcon={<ReceiptLong />}
            onClick={() => setRegistrando(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Registrar nota
          </Button>
        ) : (
          consultor && (
            <ComMotivo motivo={EXPLICACAO_NOTA_INDISPONIVEL}>
              <Button
                variant="contained"
                startIcon={<ReceiptLong />}
                disabled
                sx={{ whiteSpace: 'nowrap' }}
              >
                Registrar nota
              </Button>
            </ComMotivo>
          )
        )}
      </Stack>

      {notas.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Nenhuma nota registrada
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Emita a nota no seu sistema e registre-a aqui para a Originei efetuar o pagamento.
          </Typography>
          {consultor && disponiveis.length === 0 && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {EXPLICACAO_NOTA_INDISPONIVEL}
            </Typography>
          )}
        </Card>
      ) : (
        <Card>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Notas fiscais">
              <TableHead>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>Emissão</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Competências</TableCell>
                  <TableCell>Arquivo</TableCell>
                  <TableCell>Leitura</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell>Pendências</TableCell>
                  <TableCell>Pagamento</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notas.map((n) => {
                  const abertas = pendenciasAbertas(n)
                  const destacada = n.id === notaDestacada
                  const naoPaga = motivoNaoPaga(n)
                  const naoConfere = motivoNaoConfere(n)
                  const naoAprova = motivoNaoAprova(n)
                  const recusado = arquivoRecusado(n)
                  // Sem arquivo (ENVIADA) ou com arquivo recusado ainda em conferência: anexar de novo.
                  const podeAnexar =
                    (!n.documentoId && n.status === 'ENVIADA') || (recusado && EM_CONFERENCIA.has(n.status))
                  const temDados =
                    !!n.extracao || !!n.conferencia || (n.pendencias?.length ?? 0) > 0
                  return (
                    <TableRow
                      key={n.id}
                      hover
                      selected={destacada}
                      ref={destacada ? linhaDestacada : undefined}
                      data-destacada={destacada ? 'true' : undefined}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{n.numero}</TableCell>
                      <TableCell>{data(n.dataEmissao)}</TableCell>
                      <TableCell align="right">
                        {brl(n.valor)}
                      </TableCell>
                      <TableCell>
                        {n.competenciasCobertas
                          .map((c) => dayjs(c).format('MM/YYYY'))
                          .join(', ') || '—'}
                      </TableCell>
                      <TableCell>
                        <SeloArquivoNf nota={n} />
                      </TableCell>
                      <TableCell>
                        <ChipLeitura nota={n} podeOperar={podeOperar} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={n.motivoRecusa ?? ''} disableHoverListener={!n.motivoRecusa}>
                          <Chip
                            size="small"
                            label={STATUS_NOTA[n.status].rotulo}
                            color={STATUS_NOTA[n.status].cor}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {abertas.length > 0 ? (
                          <Tooltip title={abertas.map((p) => `${rotuloPendencia(p)}: ${p.mensagem}`).join(' · ')}>
                            <Chip
                              size="small"
                              color="warning"
                              label={abertas.length === 1 ? '1 pendência' : `${abertas.length} pendências`}
                            />
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{data(n.dataPagamento)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                          {temDados && (
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => setVendo(n)}
                              aria-label={`Ver dados da nota ${n.numero}`}
                            >
                              Ver dados
                            </Button>
                          )}
                          {podeAnexar && (
                            <Button
                              component="label"
                              size="small"
                              variant="outlined"
                              startIcon={<UploadFile />}
                              disabled={salvando}
                            >
                              Anexar arquivo
                              <input
                                hidden
                                type="file"
                                accept={EXTENSOES_ACEITAS}
                                aria-label={`Anexar arquivo da nota ${n.numero}`}
                                onChange={(e) => {
                                  const f = e.target.files?.[0]
                                  if (f) void anexar(n, f)
                                }}
                              />
                            </Button>
                          )}
                          {consultor && temPendenciaAberta(n) && EM_CONFERENCIA.has(n.status) && (
                            <>
                              {n.documentoId && !recusado && (
                                <Button
                                  component="label"
                                  size="small"
                                  variant="outlined"
                                  startIcon={<UploadFile />}
                                  disabled={salvando}
                                >
                                  Enviar arquivo corrigido
                                  <input
                                    hidden
                                    type="file"
                                    accept={EXTENSOES_ACEITAS}
                                    aria-label={`Enviar arquivo corrigido da nota ${n.numero}`}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0]
                                      if (f) void anexar(n, f, true)
                                    }}
                                  />
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<Replay />}
                                onClick={() => setRetirando(n)}
                              >
                                Retirar e reemitir
                              </Button>
                            </>
                          )}
                          {podeOperar && EM_CONFERENCIA.has(n.status) && (
                            <ComMotivo motivo={naoConfere}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FactCheck />}
                                disabled={!!naoConfere}
                                onClick={() => setConferindo(n)}
                              >
                                {n.status === 'DADOS_CONFIRMADOS' ? 'Reconferir' : 'Conferir'}
                              </Button>
                            </ComMotivo>
                          )}
                          {podeOperar && n.status === 'DADOS_CONFIRMADOS' && (
                            <ComMotivo motivo={naoAprova}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Gavel />}
                                disabled={!!naoAprova}
                                onClick={() => setAprovando(n)}
                              >
                                Aprovar fiscal
                              </Button>
                            </ComMotivo>
                          )}
                          {podeOperar && n.status === 'APROVADA_FISCAL' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Undo />}
                              onClick={() => {
                                setRevogando(n)
                                setMotivoRevogacao('')
                              }}
                            >
                              Revogar
                            </Button>
                          )}
                          {podeOperar && RECUSAVEL.has(n.status) && (
                            <>
                              <ComMotivo motivo={naoPaga}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<Payments />}
                                  disabled={!!naoPaga}
                                  onClick={() => setLiquidando(n)}
                                >
                                  Pagar
                                </Button>
                              </ComMotivo>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<Block />}
                                onClick={() => {
                                  setRecusando(n)
                                  setMotivo('')
                                }}
                              >
                                Recusar
                              </Button>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <EmitirNotaDialog
        aberto={registrando}
        disponiveis={disponiveis}
        onFechar={() => setRegistrando(false)}
        onRegistrada={() => {
          void carregar()
          onMudou?.()
        }}
      />

      <DadosNotaDialog nota={vendo} podeOperar={podeOperar} onFechar={() => setVendo(null)} />

      <ConferirNotaDialog
        nota={conferindo}
        onFechar={() => setConferindo(null)}
        onConferida={(nota) => {
          setConferindo(null)
          const abertas = pendenciasAbertas(nota)
          enqueueSnackbar(
            abertas.length > 0
              ? `Conferência registrada com pendência: ${abertas.map(rotuloPendencia).join(', ')}.`
              : 'Conferência registrada, sem pendências.',
            { variant: abertas.length > 0 ? 'warning' : 'success' }
          )
          void mudou()
        }}
      />

      <AprovarFiscalDialog
        nota={aprovando}
        onFechar={() => setAprovando(null)}
        onAprovada={() => {
          setAprovando(null)
          enqueueSnackbar('Aprovação fiscal registrada — a nota pode ser paga.', { variant: 'success' })
          void mudou()
        }}
      />

      <Dialog open={liquidando !== null} onClose={() => setLiquidando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Registrar pagamento — {liquidando?.numero}</DialogTitle>
        <DialogContent>
          <TextField
            label="Data do pagamento"
            type="date"
            value={dataPagamento}
            onChange={(e) => setDataPagamento(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLiquidando(null)}>Voltar</Button>
          <Button variant="contained" onClick={() => void liquidar()} disabled={salvando}>
            {salvando ? 'Registrando…' : 'Registrar pagamento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={recusando !== null} onClose={() => setRecusando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Recusar nota — {recusando?.numero}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            As comissões cobertas voltam a ficar disponíveis, e o consultor pode emitir uma
            nova nota depois de corrigir.
          </Typography>
          <TextField
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: CNPJ do tomador incorreto."
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecusando(null)}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void recusar()}
            disabled={salvando || !motivo.trim()}
          >
            {salvando ? 'Recusando…' : 'Recusar nota'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revogando !== null} onClose={() => setRevogando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Revogar aprovação fiscal — {revogando?.numero}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            A nota volta para “Dados confirmados” e precisa de nova aprovação fiscal antes do
            pagamento.
          </Typography>
          <TextField
            label="Motivo da revogação"
            value={motivoRevogacao}
            onChange={(e) => setMotivoRevogacao(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevogando(null)}>Voltar</Button>
          <Button
            variant="contained"
            onClick={() => void revogar()}
            disabled={salvando || !motivoRevogacao.trim()}
          >
            {salvando ? 'Revogando…' : 'Revogar aprovação'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={retirando !== null} onClose={() => setRetirando(null)} fullWidth maxWidth="sm">
        <DialogTitle>Retirar a nota {retirando?.numero}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            A nota sai da conferência e as comissões cobertas voltam ao disponível. Corrija a
            nota no seu sistema e registre a nova — o mesmo número pode ser reutilizado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetirando(null)}>Voltar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void retirar()}
            disabled={salvando}
          >
            {salvando ? 'Retirando…' : 'Retirar nota'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/**
 * Andamento da leitura; para o back-office, uma leitura concluída com divergência vira
 * pré-alerta (só a conferência humana cria pendência — AN-9 §4).
 */
const ChipLeitura: FC<{ nota: NotaFiscalComissao; podeOperar: boolean }> = ({
  nota,
  podeOperar,
}) => {
  const e = nota.extracao
  if (!e) return <>—</>
  const divergencias = e.status === 'CONCLUIDA' ? e.divergencias.length : 0
  if (podeOperar && divergencias > 0) {
    return (
      <Tooltip title={e.divergencias.map((d) => d.rotulo).join(', ')}>
        <Chip
          size="small"
          color="warning"
          label={divergencias === 1 ? '1 divergência' : `${divergencias} divergências`}
        />
      </Tooltip>
    )
  }
  const { rotulo, cor } = ROTULO_LEITURA[e.status]
  return (
    <Tooltip title={e.mensagem ?? ''} disableHoverListener={!e.mensagem}>
      <Chip size="small" variant="outlined" label={rotulo} color={cor} />
    </Tooltip>
  )
}

export default NotasFiscaisPanel
